import { CircleInfoIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Merchandise, ProductReference } from "types";
import { VariantQuantity } from "../updateQuantityHelper";
import { NonTrackedReturnInventoryCard } from "./NonTrackedReturnVariantList";
import { ReturnVariantAdjustment } from "./ReturnVariantAdjustment";
import {
    getVariantAdjustmentSync,
    hasAdjustmentRowsChanged,
    ReturnAdjustmentRow,
    ReturnAdjustmentsByTemplateId,
} from "./returnVariantHelper";
import { useProduct } from "@/hooks/resources/useProduct";
import { isInventoryTracked } from "@/models/Merchandise";

const RETURN_INVENTORY_TOOLTIP =
    "Only items that have already been fulfilled can be returned. Items that are still unfulfilled are immediately returned to available inventory when their quantity is reduced.";

interface ReturnVariantListProps {
    variants: VariantQuantity[];
    merchandiseProduct: ProductReference;
    errorByTemplateId?: Record<string, string[]>;
    onAdjustmentsChange?: (adjustments: ReturnAdjustmentsByTemplateId) => void;
    onHasIncompleteAdjustmentsChange?: (hasIncompleteAdjustments: boolean) => void;
}

interface VariantReductionValues {
    delta: number;
    unfulfilledReduction: number;
    fulfilledReduction: number;
    maxFulfilled: number;
}

interface ReturnInventoryVariantSummary {
    variant: VariantQuantity;
    reduction: VariantReductionValues;
    rows: ReturnAdjustmentRow[];
    allocatedQty: number;
    totalAllocatedQty: number;
    errorMessages: string[];
}

const getVariantReducedValues = (variant: VariantQuantity): VariantReductionValues => {
    const delta = variant.currentQuantity - variant.newQuantity;
    const unfulfilledQty = Math.max(0, variant.currentQuantity - variant.fulfilledQuantity);

    return {
        delta,
        unfulfilledReduction: Math.min(unfulfilledQty, delta),
        fulfilledReduction: Math.max(0, delta - unfulfilledQty),
        maxFulfilled: Math.min(delta, variant.fulfilledQuantity),
    };
};

export const ReturnVariantList = ({
    variants,
    merchandiseProduct,
    errorByTemplateId = {},
    onAdjustmentsChange,
    onHasIncompleteAdjustmentsChange,
}: ReturnVariantListProps) => {
    const { product: merchandise } = useProduct<Merchandise>(merchandiseProduct.id);
    const [rowsByTemplateId, setRowsByTemplateId] = useState<ReturnAdjustmentsByTemplateId>({});

    const updateRowsByTemplateId = useCallback(
        (updater: (previous: ReturnAdjustmentsByTemplateId) => ReturnAdjustmentsByTemplateId) => {
            setRowsByTemplateId((previous) => {
                const next = updater(previous);
                if (next !== previous) {
                    onAdjustmentsChange?.(next);
                }
                return next;
            });
        },
        [onAdjustmentsChange],
    );

    const reducedVariants = useMemo(() => {
        return variants.filter((variant) => variant.newQuantity < variant.currentQuantity);
    }, [variants]);

    const hasTrackedInventory = merchandise ? isInventoryTracked(merchandise) : false;

    const variantSummaries = useMemo((): ReturnInventoryVariantSummary[] => {
        const nextSummaries: ReturnInventoryVariantSummary[] = [];

        for (const variant of reducedVariants) {
            const reduction = getVariantReducedValues(variant);
            const rows = rowsByTemplateId[variant.templateId] ?? [];
            const allocatedQty = rows.reduce((sum, row) => sum + row.qty, 0);

            nextSummaries.push({
                variant,
                reduction,
                rows,
                allocatedQty,
                totalAllocatedQty: reduction.unfulfilledReduction + allocatedQty,
                errorMessages: errorByTemplateId[variant.templateId] ?? [],
            });
        }

        return nextSummaries;
    }, [errorByTemplateId, reducedVariants, rowsByTemplateId]);

    const hasIncompleteAdjustments = useMemo(() => {
        if (!hasTrackedInventory) {
            return false;
        }

        for (const summary of variantSummaries) {
            if (summary.totalAllocatedQty < summary.reduction.delta) {
                return true;
            }
        }

        return false;
    }, [hasTrackedInventory, variantSummaries]);

    useEffect(() => {
        onHasIncompleteAdjustmentsChange?.(hasIncompleteAdjustments);
    }, [hasIncompleteAdjustments, onHasIncompleteAdjustmentsChange]);

    // Add default row when fulfilled items are reduced; keep qty in sync as quantities change.
    useEffect(() => {
        if (!merchandise || !hasTrackedInventory) {
            return;
        }

        updateRowsByTemplateId((previous) => {
            const updates: ReturnAdjustmentsByTemplateId = {};

            for (const variant of reducedVariants) {
                const reduction = getVariantReducedValues(variant);
                const previousRows = previous[variant.templateId] ?? [];
                const syncedRows = getVariantAdjustmentSync(previousRows, reduction.fulfilledReduction);

                const hasChanged = hasAdjustmentRowsChanged(previousRows, syncedRows);

                if (hasChanged) {
                    updates[variant.templateId] = syncedRows;
                }
            }

            return Object.keys(updates).length > 0 ? { ...previous, ...updates } : previous;
        });
    }, [hasTrackedInventory, merchandise, reducedVariants, updateRowsByTemplateId]);

    if (!merchandise) {
        return null;
    }

    if (variantSummaries.length === 0) {
        return null;
    }

    return (
        <div className="space-y-3">
            <div className="flex items-center">
                <h3 className="font-bold">Return Inventory</h3>
                <Tooltip className="ml-1" content={RETURN_INVENTORY_TOOLTIP} placement="top">
                    <CircleInfoIcon />
                </Tooltip>
            </div>

            {variantSummaries.map((summary) => {
                const { variant, reduction, rows, allocatedQty, totalAllocatedQty, errorMessages } = summary;

                if (!hasTrackedInventory) {
                    return (
                        <NonTrackedReturnInventoryCard
                            key={variant.templateId}
                            variant={variant}
                            moveToAvailableCount={reduction.unfulfilledReduction}
                            returnRestockCount={reduction.fulfilledReduction}
                        />
                    );
                }

                return (
                    <ReturnVariantAdjustment
                        key={variant.templateId}
                        templateId={variant.templateId}
                        variant={variant}
                        maxAdjustmentQty={reduction.fulfilledReduction}
                        delta={reduction.delta}
                        unfulfilledReduction={reduction.unfulfilledReduction}
                        rows={rows}
                        allocatedQty={allocatedQty}
                        totalAllocatedQty={totalAllocatedQty}
                        errorMessages={errorMessages}
                        updateRowsByTemplateId={updateRowsByTemplateId}
                    />
                );
            })}
        </div>
    );
};
